import React from 'react';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import { IconChevronRight, IconPlus } from '@tabler/icons-react';
import { categoryDotColor } from './iconCategory';
import { sheenSx } from './shimmer';
import { useUiPrefs } from '../../contexts/UiPrefsContext';

/**
 * v4 section container (DESIGN_SYSTEM.md section 8.1, #1377). The fundamental
 * grouping primitive: one per data type per page, never nested. Raised surface +
 * hairline border, a header row with a category dot + title + optional count on
 * the left and a right cluster (subtotal / note / [+] add button), then
 * hairline-divided rows as children.
 *
 * Board paddings: shell `2px 16px` (density="default") or `2px 14px`
 * (density="compact"); `flushTable` drops the vertical padding for transaction
 * tables (`0 16px` / `0 14px`). Header `8px 0 7px` (compact `9px 0 8px`).
 *
 * Optionally collapsible: pass `collapsible` to turn the header into a toggle
 * button (chevron + aria-expanded) that shows/hides the rows via MUI Collapse.
 * `defaultCollapsed` sets the initial state (local state, so it resets on every
 * mount). Used by the Archived sections on Budgets/Ledgers (#1269).
 *
 * @param {object} props
 * @param {React.ReactNode} props.title       section label (e.g. "Checking")
 * @param {string} [props.category]           drives the dot color (checking|credit|planned|...)
 * @param {string} [props.dotColor]           explicit dot color; overrides category
 * @param {React.ReactNode} [props.count]     quiet item count after the title
 * @param {React.ReactNode} [props.subtotal]  right-aligned subtotal node (usually an AmountDisplay)
 * @param {('positive'|'positive-strong'|'negative'|'zero'|'default')} [props.subtotalTone]
 *                                             explicit subtotal color (else the node colors itself)
 * @param {React.ReactNode} [props.note]      quiet 11px note in the right cluster
 *                                             (e.g. "isolated from totals")
 * @param {function} [props.onAdd]            renders the 22x22 [+] add button
 * @param {object} [props.addButtonProps]     extra props spread on the [+] button
 *                                             (e.g. a data-tour-id guide anchor)
 * @param {React.ReactNode} [props.actions]   header controls left of the [+]
 *                                             (e.g. a category's rename / delete, #1963)
 * @param {boolean} [props.recessed]          recessed surface (Archived blocks)
 * @param {('default'|'compact')} [props.density] shell + header padding density
 * @param {boolean} [props.flushTable]        zero vertical shell padding (tables)
 * @param {boolean} [props.shimmer]            sheen-sweep the category dot
 * @param {string} [props.shimmerDelay]        per-element sheen start offset
 * @param {boolean} [props.collapsible]        make the header a collapse/expand toggle
 * @param {boolean} [props.defaultCollapsed]   initial collapsed state (only when collapsible)
 * @param {function} [props.onToggle]          called with the new open state when a
 *                                             collapsible header toggles (e.g. a lazy fetch, #1971)
 * @param {boolean} [props.edge]               category color as a 1px top edge in place of
 *                                             the dot (Dashboard groups, screen 8, #1971)
 * @param {boolean} [props.showDot]           false drops the dot with nothing in its
 *                                             place (Dashboard Cost of living, #2029)
 * @param {React.ReactNode} props.children    rows
 * @param {object} [props.sx]                 extra sx merged onto the container
 */
const SectionContainer = ({
  title,
  category = 'neutral',
  dotColor,
  count,
  subtotal,
  subtotalTone,
  note,
  onAdd,
  addButtonProps,
  actions,
  recessed = false,
  density = 'default',
  flushTable = false,
  shimmer = false,
  shimmerDelay = '0ms',
  collapsible = false,
  defaultCollapsed = false,
  onToggle,
  edge = false,
  showDot = true,
  children,
  sx,
  ...rest
}) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const dot = dotColor || categoryDotColor(theme, category);
  // #1979: the user's Icon shimmer preference gates every sheen.
  const { shimmer: shimmerPref } = useUiPrefs();
  const [collapsed, setCollapsed] = React.useState(collapsible ? defaultCollapsed : false);
  const contentId = React.useId();

  const isCompact = density === 'compact';
  const shellPad = flushTable
    ? (isCompact ? '0 14px' : '0 16px')
    : (isCompact ? '2px 14px' : '2px 16px');
  const headerPad = isCompact ? '9px 0 8px' : '8px 0 7px';

  const subtotalTones = {
    positive: palette.success.main,
    'positive-strong': palette.positiveStrong,
    negative: palette.error.main,
    zero: palette.text.dim,
    dim: palette.text.dim,
    default: palette.text.primary,
  };

  // When collapsed, drop the header's bottom rule so it reads as a single tidy
  // toggle row with nothing dangling beneath it. A container with no rows
  // (an empty budget category, #1964) drops it for the same reason.
  const hasRows = React.Children.toArray(children).length > 0;
  const headerOpen = collapsible ? !collapsed : hasRows;

  const headerSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: headerPad,
    ...(collapsible
      ? {
          // Reset native button chrome so the header looks identical to the
          // static <header> variant, just clickable.
          width: '100%',
          appearance: 'none',
          background: 'transparent',
          border: 0,
          borderBottom: headerOpen ? `1px solid ${palette.divider}` : 'none',
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          '&:focus-visible': {
            outline: `1.5px solid ${palette.interactive}`,
            outlineOffset: '2px',
          },
        }
      : {
          borderBottom: headerOpen ? `1px solid ${palette.divider}` : 'none',
        }),
  };

  const headerContent = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[2]}px`, minWidth: 0 }}>
        {collapsible && (
          <IconChevronRight
            size={13}
            aria-hidden
            style={{
              transform: collapsed ? 'none' : 'rotate(90deg)',
              transition: `transform ${ds.motion.fast} ${ds.motion.easeOut}`,
              flexShrink: 0,
              color: palette.text.dim,
              // Decorative: let clicks fall through to the toggle button. Without
              // this, a click on the rotating SVG retargets and the button's
              // onClick never fires (#1272).
              pointerEvents: 'none',
            }}
          />
        )}
        {showDot && !edge && (
          <Box
            component="span"
            sx={{
              width: ds.size.dot,
              height: ds.size.dot,
              borderRadius: '50%',
              backgroundColor: dot,
              flexShrink: 0,
              ...(shimmer && shimmerPref && sheenSx(theme, { delay: shimmerDelay })),
            }}
          />
        )}
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: `${ds.font.meta}px`,
            fontWeight: ds.font.weightMedium,
            color: palette.text.secondary,
          }}
        >
          {title}
        </Typography>
        {count != null && (
          <Typography
            component="span"
            sx={{
              fontSize: `${ds.font.meta}px`,
              fontWeight: ds.font.weightRegular,
              color: palette.text.faint,
            }}
          >
            {count}
          </Typography>
        )}
      </Box>
      {(subtotal != null || note != null || actions != null || onAdd != null) && (
        <Box
          component="span"
          sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[5]}px`, flexShrink: 0 }}
        >
          {subtotal != null && (
            <Box
              component="span"
              sx={{
                fontSize: `${ds.font.meta}px`,
                fontWeight: ds.font.weightMedium,
                fontVariantNumeric: 'tabular-nums',
                ...(subtotalTone && { color: subtotalTones[subtotalTone] }),
              }}
            >
              {subtotal}
            </Box>
          )}
          {note != null && (
            <Box component="span" sx={{ fontSize: '11px', color: palette.text.dim }}>
              {note}
            </Box>
          )}
          {actions != null && (
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
              onClick={(e) => e.stopPropagation()}
            >
              {actions}
            </Box>
          )}
          {onAdd != null && (
            <Box
              component="button"
              type="button"
              aria-label={typeof title === 'string' ? `Add to ${title}` : 'Add'}
              {...addButtonProps}
              onClick={(e) => { e.stopPropagation(); onAdd(e); }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: `${ds.radius.button}px`,
                border: `1px solid ${palette.borderStrong}`,
                background: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
                color: palette.interactive,
              }}
            >
              <IconPlus size={13} aria-hidden />
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: recessed ? palette.surface.recessed : palette.surface.raised,
        border: `1px solid ${palette.divider}`,
        ...(edge && { borderTopColor: dot }),
        borderRadius: `${ds.radius.container}px`,
        padding: shellPad,
        transition: `background-color ${ds.motion.base} ${ds.motion.easeOut}, border-color ${ds.motion.base} ${ds.motion.easeOut}`,
        ...sx,
      }}
      {...rest}
    >
      {/* v4 (#1377): headerless containers (off-card heads, e.g. Account)
          render no header row at all - no stray dot or hairline. */}
      {title != null && (collapsible ? (
        <Box
          component="button"
          type="button"
          onClick={() => {
            const open = collapsed;
            setCollapsed(!collapsed);
            if (onToggle) onToggle(open);
          }}
          aria-expanded={!collapsed}
          aria-controls={contentId}
          sx={headerSx}
        >
          {headerContent}
        </Box>
      ) : (
        <Box component="header" sx={headerSx}>
          {headerContent}
        </Box>
      ))}
      {collapsible ? (
        <Collapse in={!collapsed} unmountOnExit id={contentId}>
          {children}
        </Collapse>
      ) : (
        children
      )}
    </Box>
  );
};

export default SectionContainer;
